import { type MaybeRef, computed, unref } from 'vue'

import usePlans from '@/modules/plans/composables/usePlans'
import type { Plan, PlanFeature } from '@/modules/plans/interfaces/plan'

/**
 * ¿Este plan incluye la feature?
 *
 * Mismo criterio que `usePlanFeature.locked`, invertido, para que el candado y
 * el texto que lo explica nunca digan cosas distintas: las booleanas cuentan con
 * `included === true` y las de tope con cualquier límite que no sea 0 (null =
 * ilimitado también cuenta).
 */
const planIncludes = (plan: Plan, featureCode: string): boolean => {
	const feature = plan.features?.find((f) => f.code === featureCode)

	if (!feature) return false

	const parsed = decodeValue(feature.value)

	if (!parsed) return false

	if ('limit' in parsed) return parsed.limit !== 0

	return parsed.included === true
}

type FeatureValue = { limit?: number | null; included?: boolean }

/**
 * `value` llega de dos formas y hay que aguantar las dos.
 *
 * `/v1/plans` lo manda ya decodificado (el cast del modelo lo entrega como
 * objeto), pero la interfaz lo declara `string` y otros consumidores lo
 * JSON.parse-an. Filas legacy además vienen doble-encodeadas. Parsear a ciegas
 * dejaba a TODOS los planes como "no la incluye" y el aviso caía siempre en la
 * frase genérica.
 */
const decodeValue = (raw: PlanFeature['value']): FeatureValue | null => {
	let value: unknown = raw

	while (typeof value === 'string') {
		try {
			value = JSON.parse(value)
		} catch {
			return null
		}
	}

	return value && typeof value === 'object' ? (value as FeatureValue) : null
}

/** "PRO", "PRO y BUSINESS+", "START, PRO y BUSINESS+". */
const joinNames = (names: string[]): string => {
	if (names.length === 0) return ''
	if (names.length === 1) return names[0]

	return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`
}

/**
 * Texto del aviso que se le muestra al cliente cuando su plan no incluye una
 * función, armado con lo que se configuró en booking-internal.
 *
 * El encabezado y el detalle son datos (`features.lock_title` /
 * `lock_description`), no cadenas en el código: antes decían "PRO y BUSINESS+"
 * a mano y eso miente en cuanto la feature se mueve de plan en la matriz, se
 * renombra un plan, o se crea uno nuevo — a esa empresa el aviso le nombraba
 * planes ajenos al suyo.
 *
 * Los nombres tampoco se guardan: el encabezado trae el marcador `:planes` y se
 * sustituye aquí con los planes del catálogo **público** que sí la incluyen.
 * Que sean los públicos es deliberado y es la misma regla que aplica el backend
 * en `PlanLimitGuard`: un aviso de upsell no puede delatar un plan oculto.
 */
export default function useFeatureLockCopy(featureCode: MaybeRef<string>) {
	const { data, isLoading } = usePlans()

	const code = computed(() => unref(featureCode))

	const planNames = computed(() =>
		(data.value?.plans ?? [])
			.filter((plan) => planIncludes(plan, code.value))
			.map((plan) => plan.name)
	)

	const planList = computed(() => joinNames(planNames.value))

	/** La feature tal como viene del catálogo, para leer su copy configurado. */
	const feature = computed<PlanFeature | undefined>(() => {
		for (const plan of data.value?.plans ?? []) {
			const match = plan.features?.find((f) => f.code === code.value)
			if (match) return match
		}

		return undefined
	})

	/**
	 * Encabezado listo para pintar. Sin copy configurado se arma uno genérico
	 * en vez de inventar nombres de plan; si nadie la incluye, tampoco se
	 * promete un upgrade que no existe.
	 */
	/**
	 * Pausada = congelada para todas las empresas, sin importar el plan.
	 *
	 * Tiene que ganar sobre el aviso de upgrade: decirle "actualiza tu plan" a alguien que
	 * ya la tiene contratada es mentira, y encima le ofrece pagar por algo que no va a
	 * poder usar hasta que se reactive. El texto lo escribe quien la pausa desde
	 * booking-internal, porque es él quien sabe qué se le puede prometer al cliente.
	 */
	const isPaused = computed(() => feature.value?.isPaused === true)

	const lockTitle = computed(() => {
		if (isPaused.value) {
			return 'Estamos trabajando en esta función'
		}

		const configured = feature.value?.lockTitle

		if (configured) {
			// split/join y no replaceAll: el target de TS de este repo es anterior
			// a ES2021 y replaceAll no existe ahí.
			return planList.value ?
					configured.split(':planes').join(planList.value)
				:	'Tu plan no incluye esta función'
		}

		return planList.value ?
				`Esta función es de los planes ${planList.value}`
			:	'Tu plan no incluye esta función'
	})

	const lockDescription = computed(() => {
		if (isPaused.value) {
			return (
				feature.value?.pausedNote ??
				'La habilitaremos en cuanto esté lista. No necesitas cambiar de plan.'
			)
		}

		return feature.value?.lockDescription ?? ''
	})

	return {
		lockTitle,
		lockDescription,
		isPaused,
		planNames,
		planList,
		isLoading,
	}
}
